import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { ConsentModel } from "@/api";
import { updateConsentManagementConfigVersion } from "@/consent-management/api/updateConsentManagementConfigVersion";
import { RegionMultiSelect } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/ConfigContent/FreshpaintConfigContent/RegionMultiSelect/RegionMultiSelect";
import {
  ConsentManagerConfigSchema,
  isValidPrivacyPolicyUrl,
  PRIVACY_POLICY_URL_ERROR_MESSAGE,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { IconButton } from "@/design-system/components/IconButton/IconButton";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { ChoiceCards, ConsentModelToggle } from "../ChoiceCards";
import { ConsentLoadError } from "../ConsentLoadError";
import {
  addRegionPreset,
  applyTargetingToDraft,
  bannerOffTargetIds,
  chooseDefaultConsentModel,
  ConsentTargeting,
  COUNTRY_NAMES,
  countInactiveRules,
  countOtherTargetedConfigs,
  currentTargeting,
  findOverridingRules,
  getTargetingErrors,
  InactiveRulesChoice,
  isValidDomain,
  keepsCustomPattern,
  newSite,
  normalizeDomain,
  newTargetId,
  presetUsesRegions,
  presetUsesSites,
  RegionTarget,
  setRegions,
  SiteTarget,
  TargetingError,
  TargetingPreset,
  targetingChangesDraft,
  undetectedGaps,
  unusedRegionPresets,
} from "../targeting";
import { ConsentManagerStepProps } from "../types";
import { targetingToSave, useCurrentTargeting } from "../useCurrentTargeting";
import {
  freshpaintConsent,
  LOAD_ERROR_REASON,
  NO_WRITE_ACCESS_REASON,
  useFreshpaintConsent,
} from "../useFreshpaintConsent";

const titles: Record<TargetingPreset, string> = {
  everywhere: "Set your rules",
  regions: "Set rules by region",
  sites: "Set rules for each site",
  "sites-and-regions": "Set rules by site and region",
};

const fallbackLabels: Record<TargetingPreset, string> = {
  everywhere: "Every visitor",
  regions: "Everywhere else",
  sites: "Other sites",
  "sites-and-regions": "Other sites, everywhere else",
};

const fallbackDescriptions: Record<TargetingPreset, string> = {
  everywhere: "For every visitor.",
  regions:
    "For visitors outside these regions, including visitors whose location can’t be found.",
  sites:
    "For pages on any other site. This applies in every region, because site rules turn off Freshpaint’s built-in regional opt-in.",
  "sites-and-regions":
    "For pages on other sites, from visitors outside these regions or whose location can’t be found.",
};

const BANNER_OFF_NOTE =
  "Turned off on the Consent Manager page. Visitors this rule matches see no banner, and Freshpaint sends their events to every destination.";

const pluralRules = (count: number) =>
  `${count} ${count === 1 ? "rule" : "rules"}`;

/** Untouched rows rely on the disabled Save button instead of red text. */
function FieldError({
  errors,
  pristine = false,
}: {
  errors: TargetingError[];
  pristine?: boolean;
}) {
  if (pristine || errors.length === 0) {
    return null;
  }
  return (
    <Typography variant="body-sm" className="text-textError">
      {errors[0].message}
    </Typography>
  );
}

function RegionCard({
  region,
  takenValues,
  errors,
  bannerOff,
  disabled,
  onChange,
  onRemove,
}: {
  region: RegionTarget;
  takenValues: string[];
  errors: TargetingError[];
  bannerOff: boolean;
  disabled: boolean;
  onChange: (patch: Partial<RegionTarget>) => void;
  onRemove: () => void;
}) {
  return (
    <li className="border-borderSubtle flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <Input
            label="Region name"
            value={region.label}
            onChange={(event) => onChange({ label: event.target.value })}
            disabled={disabled}
            className="w-full"
          />
        </div>
        <ConsentModelToggle
          label={`Consent model for ${region.label || "this region"}`}
          value={region.consentModel}
          onChange={(consentModel) => onChange({ consentModel })}
          disabled={disabled}
        />
        <IconButton
          aria-label={`Remove ${region.label || "region"}`}
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2 className="size-4" />
        </IconButton>
      </div>
      <RegionMultiSelect
        values={region.regionValues}
        disabledValues={takenValues.filter(
          (value) => !region.regionValues.includes(value),
        )}
        onSelectionChange={(selected) =>
          onChange({ regionValues: selected.map(({ value }) => value) })
        }
        disabled={disabled}
        defaultOpen={region.regionValues.length === 0}
        hideUndetectedRegions
      />
      {bannerOff && <Banner type="warning">{BANNER_OFF_NOTE}</Banner>}
      <FieldError
        errors={errors}
        pristine={!region.label && region.regionValues.length === 0}
      />
    </li>
  );
}

function SiteRow({
  site,
  sharedPrivacyPolicyUrl,
  errors,
  bannerOff,
  optInRegionsApply,
  disabled,
  onChange,
  onRemove,
}: {
  site: SiteTarget;
  sharedPrivacyPolicyUrl: string;
  errors: TargetingError[];
  bannerOff: boolean;
  optInRegionsApply: boolean;
  disabled: boolean;
  onChange: (patch: Partial<SiteTarget>) => void;
  onRemove: () => void;
}) {
  return (
    <li className="border-borderSubtle flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <Input
            label="Domain"
            placeholder="example.com"
            value={site.domain}
            onChange={(event) => onChange({ domain: event.target.value })}
            onBlur={() => {
              const domain = normalizeDomain(site.domain);
              if (domain !== site.domain && isValidDomain(domain)) {
                onChange({ domain });
              }
            }}
            disabled={disabled}
            className="w-full"
          />
        </div>
        <ConsentModelToggle
          label={`Consent model for ${site.domain || "this site"}`}
          value={site.consentModel}
          onChange={(consentModel) => onChange({ consentModel })}
          disabled={disabled}
        />
        <IconButton
          aria-label={`Remove ${site.domain || "site"}`}
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2 className="size-4" />
        </IconButton>
      </div>
      <Input
        label="Privacy policy for this site (optional)"
        placeholder={
          sharedPrivacyPolicyUrl
            ? `Leave blank to use ${sharedPrivacyPolicyUrl}`
            : "https://example.com/privacy"
        }
        value={site.privacyPolicyUrl}
        onChange={(event) => onChange({ privacyPolicyUrl: event.target.value })}
        disabled={disabled}
        error={
          isValidPrivacyPolicyUrl(site.privacyPolicyUrl.trim())
            ? undefined
            : { type: "validate", message: PRIVACY_POLICY_URL_ERROR_MESSAGE }
        }
      />
      {site.customPattern &&
        (keepsCustomPattern(site) ? (
          <Typography variant="body-sm" color="subtle">
            This rule matches pages with a pattern edited on the Consent Manager
            page ({site.customPattern.pattern}). It stays as is unless you
            change the domain.
          </Typography>
        ) : (
          <Typography variant="body-sm" color="subtle">
            Saving replaces the pattern edited on the Consent Manager page with
            one for this domain.
          </Typography>
        ))}
      {optInRegionsApply && site.consentModel === "opt-out" && (
        <Typography variant="body-sm" color="subtle">
          Visitors from your opt-in regions still get opt-in on this site.
        </Typography>
      )}
      {bannerOff && <Banner type="warning">{BANNER_OFF_NOTE}</Banner>}
      <FieldError errors={errors} pristine={!site.domain.trim()} />
    </li>
  );
}

export function TargetsStep({
  data,
  context,
  updateData,
  goNext,
}: ConsentManagerStepProps) {
  const { canWrite, isLoading, loadError, retry, fetchLatestDraft } =
    useFreshpaintConsent();
  const { draft, known } = context;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { targeting, storeTargeting } = useCurrentTargeting(data, context);
  const inactiveRuleCount = countInactiveRules(draft);
  const activeRuleCount = countOtherTargetedConfigs(draft);
  const errors = getTargetingErrors(targeting, inactiveRuleCount);
  const errorsFor = (id: string | null) =>
    errors.filter((targetError) => targetError.targetId === id);
  const readOnly = canWrite === false;
  const sharedPrivacyPolicyUrl: string =
    draft?.freshpaintConfig?.privacyPolicyUrl ?? "";

  const update = (patch: (current: ConsentTargeting) => ConsentTargeting) =>
    updateData((current) =>
      storeTargeting(patch(currentTargeting(current, context))),
    );
  const updateRegion = (id: string, patch: Partial<RegionTarget>) =>
    update((current) =>
      setRegions(
        current,
        current.regions.map((region) =>
          region.id === id ? { ...region, ...patch } : region,
        ),
      ),
    );
  const updateSite = (id: string, patch: Partial<SiteTarget>) =>
    update((current) => ({
      ...current,
      sites: current.sites.map((site) =>
        site.id === id ? { ...site, ...patch } : site,
      ),
    }));
  const setDefaultConsentModel = (defaultConsentModel: ConsentModel) =>
    update((current) =>
      chooseDefaultConsentModel(current, defaultConsentModel),
    );

  const unusedPresets = unusedRegionPresets(targeting.regions);
  const usesRegions = presetUsesRegions(targeting.preset);
  const hasOptInRegion =
    usesRegions &&
    targeting.regions.some((region) => region.consentModel === "opt-in");
  const bannerOff = bannerOffTargetIds(draft);
  const overriding = findOverridingRules(draft, targeting);
  const pageRulesApply =
    activeRuleCount > 0 ||
    (inactiveRuleCount > 0 && targeting.inactiveRules === "turn-on");
  const optOutCatchesGaps =
    targeting.defaultConsentModel === "opt-out" ||
    (presetUsesSites(targeting.preset) &&
      targeting.sites.some((site) => site.consentModel === "opt-out"));
  const gapCountries = optOutCatchesGaps
    ? undetectedGaps(targeting).map((code) => COUNTRY_NAMES.get(code) ?? code)
    : [];

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const latest = await fetchLatestDraft();
      if (!latest) {
        throw new Error("Your consent banner draft couldn’t be found");
      }
      const edits = targetingToSave(data, draft, latest);
      if (edits && targetingChangesDraft(latest, edits)) {
        const next = applyTargetingToDraft(latest, edits);
        await updateConsentManagementConfigVersion({
          type: freshpaintConsent.slug,
          consentModel: next.consentModel,
          freshpaintConfig: next.freshpaintConfig,
        } as Partial<ConsentManagerConfigSchema> &
          Pick<ConsentManagerConfigSchema, "type">);
      }
      goNext();
    } catch (saveError) {
      setError(unpackRequestErrorMessage(saveError));
      setSaving(false);
    }
  }

  return (
    <WizardStepLayout
      title={titles[targeting.preset]}
      description={
        targeting.preset === "sites-and-regions"
          ? "Each visitor gets the rule that matches where they are and which site they’re on. Where a site rule and a region rule disagree, opt-in wins. These rules replace Freshpaint’s built-in opt-in for the EU, UK and US privacy states. Destination categories are shared across every rule."
          : "Each visitor gets the rule that best matches where they are and which site they’re on. These rules replace Freshpaint’s built-in opt-in for the EU, UK and US privacy states. Destination categories are shared across every rule."
      }
      primaryAction={{
        label: "Save and continue",
        onClick: save,
        loading: saving,
        disabled:
          loadError || !known || !draft || errors.length > 0 || readOnly,
        disabledReason: readOnly
          ? NO_WRITE_ACCESS_REASON
          : loadError
            ? LOAD_ERROR_REASON
            : isLoading || !known || !draft
              ? undefined
              : errors[0]?.message,
      }}
    >
      {loadError ? (
        <ConsentLoadError retry={retry} />
      ) : isLoading || !known || !draft ? (
        <LoadingSpinner className="size-6" />
      ) : (
        <>
          {inactiveRuleCount > 0 && (
            <section className="flex flex-col gap-2">
              <Typography variant="label-md" as="h3">
                {inactiveRuleCount} inactive{" "}
                {inactiveRuleCount === 1 ? "rule" : "rules"} on the Consent
                Manager page
              </Typography>
              <Typography variant="body-sm" color="subtle">
                {inactiveRuleCount === 1 ? "It’s" : "They’re"} off because
                Targeted settings mode isn’t set to Custom. Saving these rules
                sets it to Custom.
              </Typography>
              <ChoiceCards<InactiveRulesChoice>
                label="Inactive Consent Manager rules"
                value={targeting.inactiveRules}
                onChange={(inactiveRules) =>
                  update((current) => ({ ...current, inactiveRules }))
                }
                choices={[
                  {
                    value: "turn-on",
                    title: "Turn them on",
                    description:
                      "They apply alongside the wizard’s rules. When both match a visitor, a rule that checks both site and region wins over a rule that checks only one of the two. Otherwise the Consent Manager page rule wins.",
                  },
                  {
                    value: "remove",
                    title: "Remove them",
                    description: "Only the wizard’s rules apply.",
                  },
                ]}
              />
            </section>
          )}
          {presetUsesSites(targeting.preset) && (
            <section className="flex flex-col gap-2">
              <Typography variant="label-md" as="h3">
                Sites
              </Typography>
              <Typography variant="body-sm" color="subtle">
                Each rule also covers the site’s subdomains, like www.
              </Typography>
              <ul className="flex flex-col gap-2">
                {targeting.sites.map((site) => (
                  <SiteRow
                    key={site.id}
                    site={site}
                    sharedPrivacyPolicyUrl={sharedPrivacyPolicyUrl}
                    errors={errorsFor(site.id)}
                    bannerOff={bannerOff.has(site.id)}
                    optInRegionsApply={hasOptInRegion}
                    disabled={readOnly}
                    onChange={(patch) => updateSite(site.id, patch)}
                    onRemove={() =>
                      update((current) => ({
                        ...current,
                        sites: current.sites.filter(
                          (other) => other.id !== site.id,
                        ),
                      }))
                    }
                  />
                ))}
              </ul>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  leadingIcon={<Plus aria-hidden />}
                  disabled={readOnly}
                  onClick={() =>
                    update((current) => ({
                      ...current,
                      sites: [
                        ...current.sites,
                        newSite(newTargetId(), current.defaultConsentModel),
                      ],
                    }))
                  }
                >
                  Add site
                </Button>
              </div>
            </section>
          )}
          {usesRegions && (
            <section className="flex flex-col gap-2">
              <Typography variant="label-md" as="h3">
                Regions
              </Typography>
              <Typography variant="body-sm" color="subtle">
                {targeting.preset === "sites-and-regions"
                  ? "Visitors from these places get the region’s rule on other sites. On your listed sites they get opt-in if either the site or the region is opt-in."
                  : "Visitors from these places get the region’s rule."}
              </Typography>
              <ul className="flex flex-col gap-2">
                {targeting.regions.map((region) => (
                  <RegionCard
                    key={region.id}
                    region={region}
                    takenValues={targeting.regions
                      .filter((other) => other.id !== region.id)
                      .flatMap((other) => other.regionValues)}
                    errors={errorsFor(region.id)}
                    bannerOff={bannerOff.has(region.id)}
                    disabled={readOnly}
                    onChange={(patch) => updateRegion(region.id, patch)}
                    onRemove={() =>
                      update((current) =>
                        setRegions(
                          current,
                          current.regions.filter(
                            (other) => other.id !== region.id,
                          ),
                        ),
                      )
                    }
                  />
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                {unusedPresets.map((preset) => (
                  <Button
                    key={preset.id}
                    variant="outline"
                    size="sm"
                    leadingIcon={<Plus aria-hidden />}
                    disabled={readOnly}
                    onClick={() =>
                      update((current) => addRegionPreset(current, preset.id))
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  leadingIcon={<Plus aria-hidden />}
                  disabled={readOnly}
                  onClick={() =>
                    update((current) =>
                      setRegions(current, [
                        ...current.regions,
                        {
                          id: newTargetId(),
                          label: "",
                          regionValues: [],
                          consentModel: "opt-in",
                        },
                      ]),
                    )
                  }
                >
                  Custom region
                </Button>
              </div>
            </section>
          )}
          <div className="border-borderSubtle flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="flex flex-col gap-1">
              <Typography variant="label-md">
                {fallbackLabels[targeting.preset]}
              </Typography>
              <Typography variant="body-sm" color="subtle">
                {fallbackDescriptions[targeting.preset]}
              </Typography>
            </div>
            <ConsentModelToggle
              label={`Consent model for ${fallbackLabels[targeting.preset].toLowerCase()}`}
              value={targeting.defaultConsentModel}
              onChange={setDefaultConsentModel}
              disabled={readOnly}
            />
          </div>
          {gapCountries.length > 0 && (
            <Banner type="warning">
              Some visitors in {gapCountries.join(", ")} can’t be placed in a
              state or province, so they match none of your regions and can get
              an opt-out rule. Add the whole country to an opt-in region to
              cover them.
            </Banner>
          )}
          {pageRulesApply && (
            <Typography variant="body-sm" color="subtle">
              {pluralRules(activeRuleCount + inactiveRuleCount)} from the
              Consent Manager page also apply. When one of them and a wizard
              rule both match a visitor, a rule that checks both site and region
              wins over a rule that checks only one of the two. Otherwise the
              Consent Manager page rule wins.
            </Typography>
          )}
          {overriding.length > 0 && (
            <Banner type="warning">
              <div className="flex flex-col gap-1">
                <span>
                  These Consent Manager page rules win over an opt-in rule here:
                </span>
                <ul className="list-disc pl-5">
                  {overriding.map((rule) => (
                    <li key={`${rule.rule}:${rule.target}`}>
                      {rule.rule}{" "}
                      {rule.bannerOff ? "turns the banner off" : "is opt-out"}{" "}
                      for some {rule.target} visitors
                    </li>
                  ))}
                </ul>
              </div>
            </Banner>
          )}
          <FieldError errors={errorsFor(null)} />
          <Typography variant="body-sm" color="subtle">
            Change a rule’s wording or look on the{" "}
            <WizardLink
              to={freshpaintConsent.configPageUrl()}
              className="text-textActionSelected cursor-pointer underline"
            >
              Consent Manager page
            </WizardLink>
            .
          </Typography>
        </>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
